import { ArrowR as IcArrowR } from "../lib/icons";
import { ChatComposer } from "./ChatComposer";
import { INTAKE_CONTINUE_BUTTON_CLASS } from "./intakeFooterStyles";

/**
 * The single bottom bar for the intake flow. It has two mutually exclusive
 * states, driven by whether the current step still needs free-text input:
 *
 *  1. Standard — a right-aligned Continue button.
 *  2. Text entry — the composer input fully replaces the bar.
 *
 * There is intentionally no Back button: backward navigation now happens
 * through the per-step "escape hatch" tiles, which open a destructive
 * confirmation modal. Both layers are stacked in a single grid cell so the
 * bar's height stays constant as the state flips — the two layers crossfade
 * (with a small vertical slide) instead of the bar resizing.
 *
 * Pass `textEntry` on steps that can take free text; toggle `showTextEntry`
 * to move between the two states.
 */
export interface IntakeFooterBarProps {
  onContinue: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;

  // ── Text-entry state ──────────────────────────────────────────────────────
  /** Provide on steps that accept free text. Omit for pure button steps. */
  textEntry?: {
    value: string;
    placeholder: string;
    onChange: (value: string) => void;
    onSend: () => void;
  };
  /** When true (and `textEntry` is set), show the composer instead of buttons. */
  showTextEntry?: boolean;
}

const LAYER =
  "col-start-1 row-start-1 flex items-center transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none";

export function IntakeFooterBar({
  onContinue,
  continueLabel = "Continue",
  continueDisabled,
  textEntry,
  showTextEntry = false,
}: IntakeFooterBarProps) {
  const showText = Boolean(textEntry) && showTextEntry;

  return (
    <div className="grid">
      {/* Standard: right-aligned Continue */}
      <div
        aria-hidden={showText}
        className={[
          LAYER,
          "gap-3",
          showText
            ? "pointer-events-none translate-y-1 opacity-0"
            : "translate-y-0 opacity-100",
        ].join(" ")}
      >
        <span className="flex-1" />
        <button
          type="button"
          tabIndex={showText ? -1 : undefined}
          disabled={continueDisabled}
          className={INTAKE_CONTINUE_BUTTON_CLASS}
          onClick={onContinue}
        >
          {continueLabel} <IcArrowR size={16} />
        </button>
      </div>

      {/* Text entry: composer fills the bar */}
      {textEntry && (
        <div
          aria-hidden={!showText}
          tabIndex={showText ? undefined : -1}
          className={[
            LAYER,
            showText
              ? "translate-y-0 opacity-100"
              : "pointer-events-none -translate-y-1 opacity-0",
          ].join(" ")}
        >
          <div className="min-w-0 flex-1">
            {showText && (
              <ChatComposer
                value={textEntry.value}
                placeholder={textEntry.placeholder}
                onChange={textEntry.onChange}
                onSend={textEntry.onSend}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
